import { z } from "zod";

/**
 * Custom validations
 */

const validateMovel = (x: string | null) => {
  if (x === null) return true;
  const requiredFullSize = 'xxxxxxxxxxx'.length;
  const normalized = x.replaceAll(/\D/g, '');
  return normalized.length === 0 || normalized.length === requiredFullSize;
}

const validateFixo = (x: string | null) => {
  if (x === null) return true;
  const requiredFullSize = 'xxxxxxxxxx'.length;
  const normalized = x.replaceAll(/\D/g, '');
  return normalized.length === 0 || normalized.length === requiredFullSize;
}

const validateTime = (x: string | null) => {
  if (x === null) return true;
  const requiredFullSize = 'xxxxxxxx'.length;
  const normalized = x.replaceAll(/\D/g, '');
  return normalized.length === 0 || normalized.length === requiredFullSize;
}

const validateCPF = (x: string | null) => {
  if (x === null) return true;
  const requiredFullSize = 'xxxxxxxxxxx'.length;
  const normalized = x.replaceAll(/\D/g, '');

  if (normalized.length === 0) {
    return true;
  }
  if (normalized.length !== requiredFullSize) {
    return false;
  }

  let soma = 0;
  let peso = 10;

  for (let i = 0; i < 9; i++) {
    soma += parseInt(normalized[i]) * peso;
    peso--;
  }

  let resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) {
    resto = 0;
  }
  if (resto !== parseInt(normalized[9])) {
    return false;
  }

  soma = 0;
  peso = 11;
  for (let i = 0; i < 10; i++) {
    soma += parseInt(normalized[i]) * peso;
    peso--;
  }
  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) {
    resto = 0;
  }
  if (resto !== parseInt(normalized[10])) {
    return false;
  }
  return true;
}

/**
 * FuncionarioSchema
 */

export const FuncionarioSchema = z.object({
  nome: z.string().nonempty('Este campo precisa ser prenchido!'),
  dataAdmissao: z.coerce.date({
    errorMap: (issue, { defaultError }) => ({
      message: issue.code === "invalid_date" ? "Este campo precisa ser preenchido com uma data valida!" : defaultError,
    }),
  }),
  idCentroCusto: z.coerce.number({ invalid_type_error: 'Este campo precisa ser prenchido!' }),
  idCargo: z.coerce.number({ invalid_type_error: 'Este campo precisa ser prenchido!' }),
  sexo: z.string({ required_error: 'Este campo precisa ser prenchido!' }),
  dataNascimento: z.coerce.date().nullable().default(null),
  regime: z.string().nullable().default(null),
  avaliarHabilidadesEm: z.coerce.number().nullable().default(null),
  descontoSindical: z.coerce.number().nullable().default(null),
  setor: z.string().nullable().default(null),
  filial: z.string().nullable().default(null),
  email1: z.string().nullable().default(null),
  email2: z.string().nullable().default(null),
  matricula: z.string().nullable().default(null),
  telefoneMovel: z.string().nullable().default(null).refine(validateMovel, 'Telefone invalido!'),
  telefoneFixo: z.string().nullable().default(null).refine(validateFixo, 'Telefone invalido!'),
  horarioTrabalho: z.string().nullable().default(null).refine(validateTime, 'Campo invalido!'),
  horarioAlmoco: z.string().nullable().default(null).refine(validateTime, 'Campo invalido!'),
  nacionalidade: z.string().nullable().default(null),
  naturalidade: z.string().nullable().default(null),
  horarioTrabalhoSegQui: z.string().nullable().default(null).refine(validateTime, 'Campo invalido!'),
  horarioTrabalhoSex: z.string().nullable().default(null).refine(validateTime, 'Campo invalido!'),
  estado: z.string().nullable().default(null),
  cpf: z.string().nullable().default(null).refine(validateCPF, 'CPF Invalido!'),
  numeroPis: z.string().nullable().default(null),
  estadoCivil: z.string().nullable().default(null),
  salario: z.coerce.number().nullable().default(null),
  periodo: z.string().nullable().default(null),
  experiencia: z.coerce.number().nullable().default(null).transform(x => x ? x : null),
  prorrogarEm: z.coerce.number().nullable().default(null).transform(x => x ? x : null),
  aprovadoExperiencia: z.coerce.number().nullable().default(null).transform(x => typeof x === 'number' ? x : null),
  escolaridade: z.string().nullable().default(null),
  cargaHorarioMensal: z.string().nullable().default(null),
  valeTransporte: z.coerce.number().nullable().default(null),
  valorValeTransporte: z.coerce.number().nullable().default(null),
  ctps: z.string().nullable().default(null),
  ctpsSerie: z.string().nullable().default(null),
  ctpsEstado: z.string().nullable().default(null),
  ctpsDataEmissao: z.coerce.date().nullable().default(null),
  rg: z.string().nullable().default(null),
  rgDataExped: z.coerce.date().nullable().default(null),
  rgOrgaoExped: z.string().nullable().default(null),
  rgEstado: z.string().nullable().default(null),
  tituloEleitor: z.string().nullable().default(null),
  tituloZona: z.string().nullable().default(null),
  tituloSecao: z.string().nullable().default(null),
  reservista: z.string().nullable().default(null),
  nomeMae: z.string().nullable().default(null),
  nomePai: z.string().nullable().default(null),
  conjugeNome: z.string().nullable().default(null),
  conjugeRg: z.string().nullable().default(null),
  conjugeCpf: z.string().nullable().default(null),
  complementoCamisaInfo: z.string().nullable().default(null),
  complementoCalcaInfo: z.string().nullable().default(null),
  complementoCalcadoInfo: z.string().nullable().default(null),
  enderecoCep: z.string().nullable().default(null),
  enderecoCidade: z.string().nullable().default(null),
  enderecoEndereco: z.string().nullable().default(null),
  enderecoNumero: z.string().nullable().default(null),
  enderecoComplemento: z.string().nullable().default(null),
  enderecoBairro: z.string().nullable().default(null),
  enderecoEstado: z.string().nullable().default(null),
  enderecoPais: z.string().nullable().default(null),
  bancTipoInfo: z.coerce.number().nullable().default(null),
  bancBancoInfo: z.string().nullable().default(null),
  bancAgenciaInfo: z.string().nullable().default(null),
  bancContaInfo: z.string().nullable().default(null),
  bancContaDigitoInfo: z.string().nullable().default(null),
  bancOperacaoInfo: z.string().nullable().default(null),
  bancPixInfo: z.string().nullable().default(null),
  observacaoGeral: z.string().nullable().default(null),
  observacaoDocumento: z.string().nullable().default(null),
  demissaoData: z.coerce.date().nullable().default(null),
  inicioAvisoPrevio: z.coerce.date().nullable().default(null),
  fimAvisoPrevio: z.coerce.date().nullable().default(null),
  demissaoIdDemissao: z.coerce.number().nullable().default(null),
  imagem: z.instanceof(File).nullable().default(null),
  documentosEntregues: z.array(z.object({
    idDocumentoEntregue: z.coerce.number(),
    idDocumento: z.coerce.number(),
    entregue: z.coerce.boolean().default(false),
    naoSeAplica: z.coerce.boolean().default(false),
  })).nullable().default(null),
});

/**
 * FuncionarioForm
 */

export type FuncionarioForm = z.infer<typeof FuncionarioSchema>;